import { useQuery } from '@tanstack/react-query'
import { Check, Copy } from 'lucide-react'
import { useRef, useState } from 'react'
import { useWalletGate } from '@/lib/wallet-gate'
import { fetchReferralStanding } from '@/lib/api'
import { buildReferralLink } from '@/lib/referral-link'
import { cn } from '@/lib/cn'

/**
 * The user's referral link, in the sidebar.
 *
 * ── Why the link is shown rather than the code ──
 * A code has to be explained before it can be used — where to enter it, on which screen.
 * A link is the whole instruction. The code is still visible inside it for anyone who
 * wants to read it out.
 *
 * ── Why it renders nothing until there is a real code ──
 * Not a skeleton, not a placeholder. A referral link is something people copy on sight,
 * and a box showing a greyed-out or partial URL is one somebody pastes into a chat
 * before it has resolved. Absent is unambiguous; half-present is not.
 */
export function ReferralLinkCard() {
  const { isSignedIn, address } = useWalletGate()
  const [copied, setCopied] = useState(false)
  /*
   * Set when the clipboard API is unavailable and the link has been SELECTED instead,
   * so the hint tells the user to press the shortcut rather than leaving them with a
   * button that appeared to do nothing.
   */
  const [selectFallback, setSelectFallback] = useState(false)
  const linkRef = useRef<HTMLSpanElement>(null)

  const { data } = useQuery({
    queryKey: ['referral-standing'],
    queryFn: fetchReferralStanding,
    // Every request would 401 without a session, and there is no code to show anyway.
    enabled: isSignedIn,
    // The code never changes once issued, so refetching on every focus is pure noise.
    staleTime: Infinity,
    // A referral code is display-only. Retrying a server-side 500 only creates a
    // burst of identical requests in DevTools; the next sign-in/session restore will
    // make one fresh attempt naturally.
    retry: false,
    refetchOnWindowFocus: false,
  })

  const code = data?.code ?? null
  if (!code) return null

  const link = buildReferralLink(code)

  const copy = async () => {
    setSelectFallback(false)
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
      return
    } catch {
      /*
       * Falls through rather than giving up.
       *
       * writeText rejects for reasons that have nothing to do with the user: an
       * unfocused document, a permissions policy, a non-secure context. Swallowing that
       * silently — which this did at first — leaves a button that visibly does nothing
       * when clicked, and no way to tell whether it worked.
       */
    }

    // Select the text so the keyboard shortcut works, and say so.
    const node = linkRef.current
    if (node) {
      const range = document.createRange()
      range.selectNodeContents(node)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
    setSelectFallback(true)
    setTimeout(() => setSelectFallback(false), 4000)
  }

  return (
    <div>
      <p className="label mb-2 flex items-center gap-2 px-1">
        Referral Link
        <span className="h-px flex-1 bg-line" aria-hidden />
      </p>

      <div className="surface-glass-2 flex items-center gap-2 rounded-[12px] px-3 py-2.5 ring-1 ring-line">
        {/*
          `truncate` with `dir="rtl"` would keep the code visible and clip the domain,
          which sounds better but reads as broken — the string starts mid-word. Clipping
          the tail is the convention people recognise, and the copy button is what they
          actually use.
        */}
        <span
          ref={linkRef}
          className="min-w-0 flex-1 truncate font-mono text-[11px] text-accent"
          title={link}
        >
          {link}
        </span>

        <button
          type="button"
          onClick={() => void copy()}
          aria-label={copied ? 'Referral link copied' : 'Copy referral link'}
          className={cn(
            'shrink-0 rounded-[7px] p-1.5 text-muted transition-colors',
            'hover:text-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent-edge',
          )}
        >
          {copied ? (
            <Check className="size-3.5 text-positive" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
        </button>
      </div>

      {/*
       * ── The code and the wallet it belongs to, spelled out ──
       *
       * The link above is truncated — it has to be, at this column width — so the code it
       * carries is exactly the part that gets clipped. Anyone reading their code out, typing
       * it into another device, or checking which wallet it is tied to could not do any of
       * those from the link alone.
       *
       * The address is shown middle-truncated rather than clipped at the end: the last four
       * characters are how people verify a wallet at a glance, so cutting the tail would
       * remove the half that does the identifying.
       */}
      <dl className="mt-2 space-y-1 px-1">
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-[10px] uppercase tracking-wide text-faint">Code</dt>
          <dd className="min-w-0 truncate font-mono text-[11px] font-semibold text-figure" title={code}>
            {code}
          </dd>
        </div>
        {address && (
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-[10px] uppercase tracking-wide text-faint">Wallet</dt>
            <dd className="min-w-0 truncate font-mono text-[11px] text-muted" title={address}>
              {`${address.slice(0, 6)}…${address.slice(-4)}`}
            </dd>
          </div>
        )}
      </dl>

      {selectFallback && (
        <p className="mt-1.5 px-1 text-[10px] leading-relaxed text-muted">
          Selected — press {navigator.platform.includes('Mac') ? '\u2318C' : 'Ctrl+C'} to copy.
        </p>
      )}

      {/*
        A live region, so the outcome reaches a screen reader too. The icon swap alone is
        invisible to one, and copying is the entire purpose of this component.
      */}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Referral link copied to clipboard' : ''}
        {selectFallback ? 'Referral link selected. Use your copy shortcut.' : ''}
      </span>
    </div>
  )
}
